'use client';
import React from 'react';

type StepProps = {
  step: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  isReversed?: boolean;
  isLast?: boolean;
};

function HowItWorksStep({ step, icon: Icon, title, description, isReversed = false, isLast = false }: StepProps) {
  return (
    <div className="tw-relative tw-group">
      {/* Mobile Layout */}
      <div className="lg:tw-hidden tw-flex tw-flex-col tw-items-center tw-text-center tw-space-y-6">
        {/* Icon with animated background */}
        <div className="tw-relative">
          <div className="tw-w-20 tw-h-20 tw-bg-gradient-to-r tw-from-emerald-400 tw-to-cyan-500 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-rotate-3 tw-transition-all tw-duration-500 group-hover:tw-rotate-6 group-hover:tw-scale-110">
            <Icon className="tw-w-10 tw-h-10 tw-text-white tw-drop-shadow-sm" />
          </div>
          {/* Friendly step bubble */}
          <div className="tw-absolute tw--top-3 tw--right-3 tw-w-10 tw-h-10 tw-bg-gradient-to-br tw-from-orange-400 tw-to-pink-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-transition-all tw-duration-300 group-hover:tw-animate-bounce">
            <span className="tw-text-white tw-font-bold tw-text-sm tw-drop-shadow-sm">{step}</span>
          </div>
        </div>

        {/* Content */}
        <div className="tw-max-w-sm tw-space-y-3">
          <h3 className="tw-text-xl tw-font-bold tw-text-gray-800 tw-font-['Lexend'] tw-leading-tight">{title}</h3>
          <p className="tw-text-gray-600 tw-font-['Lexend'] tw-leading-relaxed tw-text-base">{description}</p>
        </div>

        {/* Connection line for mobile */}
        {!isLast && (
          <div className="tw-flex tw-justify-center tw-pt-8">
            <div className="tw-w-0.5 tw-h-12 tw-bg-gradient-to-b tw-from-emerald-300 tw-to-cyan-300 tw-rounded-full tw-opacity-60"></div>
          </div>
        )}
      </div>

      {/* Desktop Layout */}
      <div className="tw-hidden lg:tw-grid lg:tw-grid-cols-12 tw-gap-8 tw-items-center tw-py-8">
        {/* Icon Section */}
        <div
          className={`tw-col-span-4 tw-flex ${
            isReversed ? 'tw-justify-start tw-order-2' : 'tw-justify-end tw-order-1'
          }`}
        >
          <div className="tw-relative">
            {/* Decorative background circle */}
            <div className="tw-absolute tw-inset-0 tw-w-28 tw-h-28 tw-bg-gradient-to-br tw-from-emerald-100 tw-to-cyan-100 tw-rounded-full tw-scale-150 tw-opacity-20 tw-transition-all tw-duration-700 group-hover:tw-scale-175 group-hover:tw-opacity-30"></div>

            {/* Main icon container */}
            <div className="tw-relative tw-w-28 tw-h-28 tw-bg-gradient-to-br tw-from-emerald-400 tw-to-cyan-500 tw-rounded-3xl tw-flex tw-items-center tw-justify-center tw-shadow-xl tw-transition-all tw-duration-500 group-hover:tw-shadow-2xl group-hover:tw-scale-105 tw-rotate-2 group-hover:tw-rotate-3">
              <Icon className="tw-w-14 tw-h-14 tw-text-white tw-drop-shadow-lg" />
            </div>

            {/* Friendly step number */}
            <div className="tw-absolute tw--top-4 tw--right-4 tw-w-12 tw-h-12 tw-bg-gradient-to-br tw-from-orange-400 tw-to-pink-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-transition-all tw-duration-300 group-hover:tw-animate-pulse">
              <span className="tw-text-white tw-font-bold tw-text-lg tw-drop-shadow-sm">{step}</span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div
          className={`tw-col-span-8 tw-space-y-4 ${
            isReversed ? 'tw-order-1 tw-text-right' : 'tw-order-2 tw-text-left'
          }`}
        >
          <h3 className="tw-text-3xl tw-font-bold tw-text-gray-800 tw-font-['Lexend'] tw-leading-tight tw-transition-colors tw-duration-300 group-hover:tw-text-emerald-600">
            {title}
          </h3>
          <p className="tw-text-base tw-text-gray-600 tw-leading-relaxed tw-font-['Lexend'] tw-max-w-md tw-transition-colors tw-duration-300 group-hover:tw-text-gray-700">
            {description}
          </p>

          {/* Decorative accent line */}
          <div
            className={`tw-w-16 tw-h-1 tw-bg-gradient-to-r tw-from-emerald-400 tw-to-cyan-500 tw-rounded-full tw-transition-all tw-duration-500 group-hover:tw-w-24 ${
              isReversed ? 'tw-ml-auto' : ''
            }`}
          ></div>
        </div>
      </div>
    </div>
  );
}

export default HowItWorksStep;
